import { createTheme, alpha } from "@mui/material/styles";
import colors from "./colors";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    modern: true;
    classic: true;
  }
}

const theme = createTheme({
  typography: {
    h1: {
      fontFamily: "Montserrat, sans-serif",
      fontWeight: "medium",
      lineHeight: "76px",
      letterSpacing: "0px",
    },
    h2: {
      fontFamily: "Montserrat, sans-serif",
      fontWeight: "medium",
      fontSize: "3.25rem",
      lineHeight: "56px",
      letterSpacing: "0px",
      color: colors.white,
      marginBottom: "1rem",
      textAlign: "center",
    },
    h3: {
      fontFamily: "Montserrat, sans-serif",
      fontWeight: "medium",
      fontSize: "2.125rem",
      lineHeight: "44px",
      letterSpacing: "0px",
      marginBottom: "1rem",
      color: colors.white,
      textAlign: "center",
    },
    h4: {
      fontFamily: "Montserrat, sans-serif",
      fontWeight: "medium",
      fontSize: "1.75rem",
      lineHeight: "38px",
      letterSpacing: "0px",
      color: colors.white,
      marginBottom: "1rem",
      textAlign: "center",
    },

    body1: {
      fontFamily: "Montserrat, sans-serif",
      fontWeight: "regular",
      fontSize: "1.375rem",
      lineHeight: "28px",
      letterSpacing: "0px",
      color: colors.white,
    },
    body2: {
      fontFamily: "Montserrat, sans-serif",
      fontWeight: "regular",
      fontSize: "1rem",
      lineHeight: "20px",
      letterSpacing: "0px",
      color: colors.white,
    },
    subtitle1: {
      fontFamily: "Montserrat, sans-serif",
      fontSize: "16px",
      lineHeight: "20px",
      letterSpacing: "0px",
    },
    caption: {
      fontFamily: "Montserrat, sans-serif",
      fontWeight: "medium",
      fontSize: "14px",
      lineHeight: "20px",
      letterSpacing: "0px",
      color: colors.primary_dark,
    },
  },
  breakpoints: {
    values: {
      xs: 0, //For mobile devices (0 < W < 480)
      sm: 481, //iPads and Tablets (480 < W < 768)
      md: 769, //Laptops and small screens (768 < W < 1024)
      lg: 1025, //Large screens and Desktops (1024 < W < 1200)
      xl: 1201, //TV and Extra Large Screens (W > 1200)
    },
  },
  palette: {
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.secondary,
    },
    error: {
      main: colors.red,
    },
    background: {
      default: colors.primary_dark,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
      disabled: colors.textDisabled,
    },
  },
  components: {
    MuiButton: {
      variants: [
        {
          props: { variant: "modern" },
          style: {
            backgroundColor: colors.accent,
            color: colors.primary_dark,
            padding: "3px 16px",
            border: `1px solid ${colors.accent}`,
            "&:hover": {
              border: `1px solid ${colors.accent}`,
              backgroundColor: alpha(colors.accent, 0.8),
            },
          },
        },
        {
          props: { variant: "classic" },
          style: {
            backgroundColor: colors.primary_dark,
            color: colors.white,
            padding: "3px 16px",
            border: `1px solid ${colors.primary_dark}`,
            "&:hover": {
              border: `1px solid ${colors.primary_dark}`,
              backgroundColor: alpha(colors.primary_dark, 0.8),
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          fontFamily: "Montserrat, sans-serif",
          fontWeight: "medium",
          textTransform: "none",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: colors.white,
          backgroundColor: alpha(colors.white, 0.1),
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(colors.white, 0.3),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.accent,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.accent,
          },
          input: {
            color: colors.white,
          },
          textarea: {
            color: colors.white,
          },
          "&.Mui-disabled": {
            "input, textarea": {
              color: colors.primary_light,
              WebkitTextFillColor: colors.primary_light,
            },
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary_light,
              WebkitTextFillColor: colors.primary_light,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: colors.white,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: colors.btn_dark_disabled,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&[data-shrink="false"]': {
            color: colors.white,
          },
          ".Mui-focused, .MuiFormLabel-filled": {
            color: colors.white,
          },
          "&:hover": {
            color: colors.white,
          },
          "&.Mui-disabled": {
            color: `${colors.primary_light} !important`,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: colors.accent,
          "&:hover": {
            color: colors.accent,
            "& .MuiTableSortLabel-icon": {
              color: colors.accent,
            },
          },
          "&.Mui-active": {
            color: colors.accent,
            "& .MuiTableSortLabel-icon": {
              color: colors.accent,
            },
          },
        },

        icon: {
          color: colors.accent,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: colors.white,
          borderColor: colors.white,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          color: colors.primary,
          "& .MuiTableCell-root": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: colors.accent,
        },
        selectIcon: {
          color: colors.accent,
        },
        select: {
          color: colors.accent,
        },
        actions: {
          "& .MuiIconButton-root": {
            color: colors.accent,
          },
          "& .MuiIconButton-root.Mui-disabled": {
            color: colors.primary_medium,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.accent,
          "&.Mui-checked": {
            color: colors.accent,
          },
          "&.MuiCheckbox-indeterminate": {
            color: colors.secondary,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.accent,
          "&.Mui-checked": {
            color: colors.accent,
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary,
        },
        message: {
          color: colors.accent,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: colors.white,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.secondary,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.white,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.white,
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: colors.white,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.primary_dark,
          border: `1px solid ${colors.gray200}`,
          borderLeft: `4px solid ${colors.primary_dark}`,
          borderRadius: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          maxHeight: 370,
          overflowY: "auto",
          "& .MuiMenuItem-root": {
            color: colors.white,
            fontSize: "0.875rem",
            "&:hover": {
              backgroundColor: colors.secondary,
            },
            "&.Mui-selected": {
              backgroundColor: colors.secondary,
              color: colors.primary_dark,
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: `${colors.white} !important`,
            "& .MuiSelect-select": {
              color: `${colors.primary_light} !important`,
              WebkitTextFillColor: `${colors.primary_light} !important`,
            },
          },
        },
      },
    },
  },
});

// RESPONSIVE TYPOGRAPHY
theme.typography.h1 = {
  [theme.breakpoints.up("xs")]: {
    fontSize: "2.5rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "3rem",
  },
  [theme.breakpoints.up("lg")]: {
    fontSize: "3.5rem",
  },
  [theme.breakpoints.up("xl")]: {
    fontSize: "4.5rem",
  },
};

theme.typography.h2 = {
  ...theme.typography.h2,
  [theme.breakpoints.down("lg")]: {
    fontSize: "2.5rem",
    lineHeight: "44px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
    lineHeight: "36px",
  },
};

theme.typography.h3 = {
  ...theme.typography.h3,
  [theme.breakpoints.down("lg")]: {
    fontSize: "1.875rem",
    lineHeight: "36px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
    lineHeight: "30px",
  },
};

theme.typography.h4 = {
  ...theme.typography.h4,
  [theme.breakpoints.down("lg")]: {
    fontSize: "1.5rem",
    lineHeight: "30px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.25rem",
    lineHeight: "26px",
  },
};

theme.typography.body1 = {
  ...theme.typography.body1,
  [theme.breakpoints.down("lg")]: {
    fontSize: "1.25rem",
    lineHeight: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.125rem",
    lineHeight: "22px",
  },
};

theme.typography.body2 = {
  ...theme.typography.body2,
  [theme.breakpoints.down("lg")]: {
    fontSize: "0.9375rem",
    lineHeight: "18px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
    lineHeight: "16px",
  },
};

theme.typography.caption = {
  ...theme.typography.caption,
  [theme.breakpoints.down("lg")]: {
    fontSize: "13px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
};

export default theme;
